import _ from 'lodash';
import { lsApiService, lsErrorService } from 'ls-vue-common';
import {
  INSTANT_QUOTE__MUTATE_LOADING_STATUS,
  INSTANT_QUOTE__MUTATE_CURRENT_QUOTE,
  INSTANT_QUOTE__RESET_QUOTE,
  INSTANT_QUOTE__GENERATE_QUOTE,
  INSTANT_QUOTE__ACCEPT_QUOTE,
  INSTANT_QUOTE__ACCEPT_PENDING_QUOTE,
  INSTANT_QUOTE__REJECT_QUOTE,
  INSTANT_QUOTE__SUGGESTED_JOB_CLICK,
  INSTANT_QUOTE__GET,
} from '@/store/types';
import { lsInstantQuoteStoreHelpers } from './instant-quote.helpers';

const createInitialState = () => {
  return {
    currentQuote: null,
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
    },
  };
};

export const mutations = {
  [INSTANT_QUOTE__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },

  [INSTANT_QUOTE__MUTATE_CURRENT_QUOTE](state, payload) {
    state.currentQuote = payload;
  },
};

export const actions = {
  [INSTANT_QUOTE__GET]({ commit, rootState }, { propertyId, instantQuoteId, include = [] }) {
    commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });

    const { customer_id } = rootState.auth.user;
    return lsApiService
      .request({
        method: 'GET',
        route: `/customers/${customer_id}/properties/${propertyId}/instant-quotes/${instantQuoteId}`,
        params: { include },
      })
      .then(({ data }) => {
        commit(INSTANT_QUOTE__MUTATE_CURRENT_QUOTE, data);
      })
      .catch((error) => {
        lsErrorService.log({ error });
      })
      .finally(() => {
        commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },
  [INSTANT_QUOTE__RESET_QUOTE]({ commit }) {
    commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });
    commit(INSTANT_QUOTE__MUTATE_CURRENT_QUOTE, null);
    commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
      isUpdating: false,
    });
  },

  [INSTANT_QUOTE__GENERATE_QUOTE](
    { commit, rootState },
    { property_id, type, formData, onSuccess = _.noop }
  ) {
    const payloadHelperDict = {
      instant_quote_bush_trimming: lsInstantQuoteStoreHelpers.formatQuotePayloadBushTrimming,
      instant_quote_leaf_removal: lsInstantQuoteStoreHelpers.formatQuotePayloadLeafRemoval,
      instant_quote_flower_bed_weeding:
        lsInstantQuoteStoreHelpers.formatQuotePayloadFlowerBedWeeding,
    };

    const payload = payloadHelperDict[type]({ formData });

    commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/${_.kebabCase(type)}s`,
        payload,
      })
      .then(({ data }) => {
        commit(INSTANT_QUOTE__MUTATE_CURRENT_QUOTE, data);
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [INSTANT_QUOTE__ACCEPT_QUOTE]({ commit, state, rootState }, { property_id, onSuccess = _.noop }) {
    commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;
    const { id } = state.currentQuote;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/instant-quotes/${id}/accept`,
      })
      .then(({ data }) => {
        if (window.isLSCustAppWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              status: 'instantQuoteAccepted',
            })
          );
        }
        commit(INSTANT_QUOTE__MUTATE_CURRENT_QUOTE, data);
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [INSTANT_QUOTE__ACCEPT_PENDING_QUOTE](
    { commit, rootState },
    { property_id, instantQuoteId, selected_date, onSuccess = _.noop }
  ) {
    commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;
    const payload = selected_date ? { selected_return_date: selected_date } : null;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/instant-quotes/${instantQuoteId}/accept`,
        payload,
      })
      .then(() => {
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [INSTANT_QUOTE__REJECT_QUOTE](
    { commit, state, rootState },
    { property_id, formData, onSuccess = _.noop }
  ) {
    commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;
    const { id } = state.currentQuote;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/instant-quotes/${id}/reject`,
        payload: {
          ...formData,
        },
      })
      .then(() => {
        if (window.isLSCustAppWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              status: 'instantQuoteRejected',
            })
          );
        }
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },
  [INSTANT_QUOTE__SUGGESTED_JOB_CLICK](
    { commit, rootState },
    { property_id, instant_quote_suggestion_id, onSuccess = _.noop }
  ) {
    commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/instant-quote-suggestions/${instant_quote_suggestion_id}/clicked`,
      })
      .then(() => {
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(INSTANT_QUOTE__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },
};

export const instantQuote = {
  state: createInitialState,
  mutations,
  actions,
};
